import { useEffect, useRef } from "react";
import type { CollaborationConversationListResponse } from "../services/collaboration";

export const AGENT_STATUS_POLL_INTERVAL_MS = 15 * 1000;

export interface AgentStatusPollingOptions {
  enabled: boolean;
  fetchSummaries: () => Promise<CollaborationConversationListResponse>;
  onRefresh: (response: CollaborationConversationListResponse) => void;
  intervalMs?: number;
}

export interface AgentStatusPollingControls {
  reconcileNow: () => void;
}

/** Keep agent inventory summaries synchronized with backend-reported state. */
export function useAgentStatusPolling(
  {
    enabled,
    fetchSummaries,
    onRefresh,
    intervalMs = AGENT_STATUS_POLL_INTERVAL_MS,
  }: AgentStatusPollingOptions,
): AgentStatusPollingControls {
  const pollRef = useRef<(queueIfBusy?: boolean) => Promise<void>>(
    async () => undefined,
  );

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    let intervalId: number | null = null;
    let inFlight = false;
    let pendingReconciliation = false;
    const isHidden = () => document.visibilityState === "hidden";

    const stop = () => {
      if (intervalId === null) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };

    const poll = async (queueIfBusy = false) => {
      if (!active || isHidden()) {
        return;
      }
      if (inFlight) {
        // Periodic ticks may coalesce, but an explicit reconnect/visibility
        // reconciliation must survive a request already in flight. One
        // trailing refresh is enough even if several reconnects arrive.
        if (queueIfBusy) pendingReconciliation = true;
        return;
      }

      inFlight = true;
      try {
        const response = await fetchSummaries();
        if (active) onRefresh(response);
      } catch {
        // Preserve the last backend-reported states on transient failure. A
        // failed refresh must never make the whole team appear idle/unknown.
      } finally {
        inFlight = false;
        const shouldReconcile = pendingReconciliation;
        pendingReconciliation = false;
        if (
          shouldReconcile &&
          active &&
          !isHidden()
        ) {
          void poll();
        }
      }
    };
    pollRef.current = poll;

    const start = () => {
      if (isHidden() || intervalId !== null) return;
      intervalId = window.setInterval(() => {
        void poll();
      }, intervalMs);
    };

    const handleVisibilityChange = () => {
      if (isHidden()) {
        stop();
        return;
      }

      void poll(true);
      start();
    };

    start();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      active = false;
      pendingReconciliation = false;
      pollRef.current = async () => undefined;
      stop();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, fetchSummaries, intervalMs, onRefresh]);

  return {
    reconcileNow: () => {
      void pollRef.current(true);
    },
  };
}
